import { useAuth } from './useAuth'
import { supabase } from '@/integrations/supabase/client'
import { useState, useEffect, createContext, useContext } from 'react'

interface AdminAuthContextType {
  isAdmin: boolean
  role: string | null
  loading: boolean
}

const AdminAuthContext = createContext<AdminAuthContextType>({ isAdmin: false, role: null, loading: true })

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [role, setRole] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setIsAdmin(false)
      setRole(null)
      setLoading(false)
      return
    }

    let cancelled = false
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .in('role', ['admin', 'owner'])
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return
        if (data && !error) {
          setIsAdmin(true)
          setRole(data.role)
        } else {
          setIsAdmin(false)
          setRole(null)
        }
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [user, authLoading])

  return (
    <AdminAuthContext.Provider value={{ isAdmin, role, loading }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export const useAdminAuth = () => useContext(AdminAuthContext)
